import { type CurrencyTypeEnum, useCurrenciesQuery, type WalletCurrency } from '@capsicum/common';
import React, { useCallback, useEffect, useState } from 'react';

import { Resource } from '../../../atoms/resource/Resource';
import { BaseSelectInput, type BaseSelectInputProps } from '../select-input/BaseSelectInput';

export type BaseCurrencyInputProps = Omit<
	BaseSelectInputProps<WalletCurrency>,
	'data' | 'itemTemplate' | 'onSearch'
> & {
	typeOfCurrency?: CurrencyTypeEnum;
	withoutInitialValue?: boolean;
};

export const BaseCurrencySelect = ({
	labelProp = 'shortCode',
	onChange,
	typeOfCurrency,
	value,
	withoutInitialValue,
	...props
}: BaseCurrencyInputProps) => {
	const currencies = useCurrenciesQuery();

	const [selectedItem, setSelectedItem] = useState<WalletCurrency>();

	useEffect(() => {
		if (value) {
			setSelectedItem(value);
		} else if (!withoutInitialValue) {
			const initialValue = typeOfCurrency
				? currencies?.data?.filter(item => item.type === typeOfCurrency)[0]
				: currencies?.data?.[0];

			setSelectedItem(initialValue);
			if (initialValue) {
				onChange?.(initialValue);
			}
		}
	}, [withoutInitialValue, value, typeOfCurrency, currencies?.data]);

	const itemTemplate = useCallback(({ item }: { item: WalletCurrency }) => {
		return `${item.shortCode} - ${item.label}`;
	}, []);

	const onSearch = useCallback((data: Array<WalletCurrency>, text: string) => {
		return data.filter(
			({ label, shortCode }) =>
				label?.toLowerCase().includes(text.toLowerCase()) ||
				shortCode?.toLowerCase().includes(text.toLowerCase())
		);
	}, []);

	return (
		<Resource r={currencies}>
			{data => (
				<BaseSelectInput
					data={typeOfCurrency ? data.filter(item => item.type === typeOfCurrency) : data}
					itemTemplate={itemTemplate}
					labelProp={labelProp}
					value={selectedItem}
					onChange={onChange}
					onSearch={onSearch}
					{...props}
				/>
			)}
		</Resource>
	);
};
